import InputField from "@components/InputField";
import Button from "@components/Button";
import { FormContext } from "@providers/FormProvider";
import { useState, useContext, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import StripePaymentForm from "./StripePaymentForm";
// import { FaArrowCircleLeft } from "react-icons/fa";
import { IoChevronBack } from "react-icons/io5";

export default function CheckoutForm({ cartItems }) {
	const { formData, updateFormData } = useContext(FormContext);
	const [tempFormData, setTempFormData] = useState(formData);
	const [error, setError] = useState(null);

	// Load saved form data on mount
	useEffect(() => {
		const savedFormData = localStorage.getItem("formData");
		if (savedFormData) {
			const parsedData = JSON.parse(savedFormData);
			setTempFormData(parsedData);
			updateFormData(parsedData); // Ensure context is updated with saved data
		}
	}, [updateFormData]);

	// Single ref to track changes
	const hasChanges = useRef(false);

	// Handle input changes
	const handleInputChange = (e) => {
		const { name, value } = e.target;
		hasChanges.current = true;
		const updatedData = {
			...tempFormData,
			[name]: value,
		};
		setTempFormData(updatedData);
		updateFormData(updatedData); // Update context immediately
	};

	const [showPayment, setShowPayment] = useState(false);

	// Save to localStorage and context on unmount
	useEffect(() => {
		return () => {
			if (hasChanges.current) {
				localStorage.setItem("formData", JSON.stringify(tempFormData));
				Object.entries(tempFormData).forEach(([key, value]) => {
					updateFormData(key, value);
				});
			}
		};
	}, [tempFormData, updateFormData]);

	const handleFormSubmit = (e) => {
		e.preventDefault(); // Prevent form submission

		// Validation logic
		if (tempFormData.firstName.length > 15) {
			setError("First name cannot be more than 15 characters.");
			return;
		}
		// Update the lastName validation to handle empty values
		if (tempFormData.lastName && tempFormData.lastName.length > 15) {
			setError("Last name cannot be more than 15 characters.");
			return;
		}
		const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailPattern.test(tempFormData.email)) {
			setError("Please enter a valid email address.");
			return;
		}
		const today = new Date();
		const birthDate = new Date(tempFormData.dob);
		let age = today.getFullYear() - birthDate.getFullYear();
		const monthDifference = today.getMonth() - birthDate.getMonth();
		if (
			monthDifference < 0 ||
			(monthDifference === 0 && today.getDate() < birthDate.getDate())
		) {
			age--;
		}
		if (age < 16) {
			setError("You must be at least 16 years old.");
			return;
		}
		const isValidMobile = /^04\d{8,9}$/.test(tempFormData.contact);
		const isValidEmergencyContact =
			tempFormData.emergencyContact === "" ||
			/^04\d{8,9}$/.test(tempFormData.emergencyContact);

		if (!isValidMobile) {
			setError("Please enter a valid Australian mobile number.");
			return;
		}
		if (tempFormData.emergencyContact && !isValidEmergencyContact) {
			setError("Please enter a valid Australian emergency contact number.");
			return;
		}
		if (
			tempFormData.emergencyContact &&
			tempFormData.emergencyContact === tempFormData.contact
		) {
			setError("Emergency contact cannot be the same as the mobile number.");
			return;
		}
		if (!tempFormData.drivingExperience) {
			setError("Driving Experience is required.");
			return;
		}

		// Clear error message
		setError(null);

		// Save to localStorage and update context
		localStorage.setItem("formData", JSON.stringify(tempFormData));
		setShowPayment(true);
	};

	const handleBackClick = () => {
		setShowPayment(false);
	};

	return (
		<div className="flex justify-center items-center flex-col space-y-12 w-full">
			<h1 className="font-[600] font-generalSans text-center text-steerBlack text-4xl dark:text-white">
				{!showPayment ? "Student Details" : "Payment Details"}
			</h1>
			{!showPayment ? (
				<div className="w-full justify-end font-poppins p-8 bg-white/60 backdrop-blur-md rounded-2xl">
					<form
						className="grid grid-cols-1 md:grid-cols-2 gap-4"
						onSubmit={handleFormSubmit}
					>
						<InputField
							label="First Name"
							type="text"
							name="firstName"
							value={tempFormData.firstName || ""}
							required={true}
							onChange={handleInputChange}
							placeholder="John (must not be more than 15 characters)"
						/>
						<InputField
							label="Last Name"
							type="text"
							name="lastName"
							value={tempFormData.lastName || ""}
							onChange={handleInputChange}
							placeholder="Doe (must not be more than 15 characters)"
						/>
						<InputField
							label="Contact"
							type="tel"
							name="contact"
							value={tempFormData.contact || ""}
							required={true}
							onChange={handleInputChange}
							placeholder="starting with 04 (10 or 11 digits)"
						/>
						<InputField
							label="Email"
							type="email"
							name="email"
							value={tempFormData.email || ""}
							required={true}
							onChange={handleInputChange}
							placeholder="john@doe.com"
						/>
						<InputField
							label="Date of Birth"
							type="date"
							name="dob"
							value={tempFormData.dob || ""}
							onChange={handleInputChange}
						/>
						<InputField
							label="Emergency Contact"
							type="text"
							name="emergencyContact"
							value={tempFormData.emergencyContact || ""}
							onChange={handleInputChange}
							placeholder="starting with 04 (10 or 11 digits)"
						/>
						<InputField
							label="Transmission Preference"
							type="select"
							name="transmissionPreference"
							value={tempFormData.transmissionPreference || ""}
							onChange={handleInputChange}
							options={[
								{ value: "automatic", label: "Automatic" },
								{ value: "manual", label: "Manual" },
							]}
						/>
						<InputField
							label="Driving Experience"
							type="select"
							name="drivingExperience"
							value={tempFormData.drivingExperience || ""}
							required={true}
							onChange={handleInputChange}
							options={[
								{ value: "none", label: "No Experience" },
								{ value: "beginner", label: "Beginner" },
								{ value: "intermediate", label: "Intermediate" },
								{ value: "advanced", label: "Advanced" },
							]}
						/>
						<InputField
							label="Lesson Date"
							type="date"
							name="lessonDate"
							value={tempFormData.lessonDate || ""}
							onChange={handleInputChange}
							min={new Date().toISOString().split("T")[0]} // Disable past dates
						/>
						{/* textfeild for any message from student */}
						<InputField
							label="Message"
							type="text"
							name="message"
							value={tempFormData.message || ""}
							onChange={handleInputChange}
							placeholder="Any message for the instructor"
						/>
						<div className="col-span-1 md:col-span-2 flex justify-center">
							<div className="flex flex-col space-y-6 items-center justify-between">
								{error && (
									<div className="text-red-500 text-sm mb-4">{error}</div>
								)}
								<Button
									buttonType="submit"
									buttonName="Proceed to Payment"
									buttonStyle="secondary"
									disabled={showPayment}
								/>
							</div>
						</div>
					</form>
				</div>
			) : (
				<div className="w-full max-w-md mx-auto p-6 flex flex-col space-y-6">
					<div
						className="flex items-center gap-2 cursor-pointer group"
						onClick={handleBackClick}
					>
						<IoChevronBack className="" />
						<span className="group-hover:-ml-2 duration-300 ease-in transition-all">
							back to student details
						</span>
					</div>
					<StripePaymentForm formData={tempFormData} cartItems={cartItems} />
				</div>
			)}
		</div>
	);
}

CheckoutForm.propTypes = {
	cartItems: PropTypes.array,
};
